import React from "react";
import { Form, Badge, Card } from "react-bootstrap";
import { XCircleFill } from "react-bootstrap-icons";
import GenericSearchBar from "../../../../MultiRole/GenericSearchBar/GenericSearchBar";
import { useLanguage } from "../../../../../../API/Context/LanguageContext";
import SkillsService from "../../../../../../API/Services/SkillsService";
import { VocabSkills } from "../../../../../../ConfigFiles/multilingual/VocabSkills";
import ColorSpecialties from "../../../../../../ConfigFiles/colors/color_specialties";
import type { SkillRead } from "../../../../../../Types/schema-helpers";
import type { SkillMetadata } from "../StepSkills";

interface SkillsBadgeSectionProps {
  activeSkills: string[];
  skillsMeta: Record<string, SkillMetadata>;
  skillsService: SkillsService;
  onAddSkill: (skill: SkillRead) => void;
  onRemoveSkill: (skillId: string) => void;
}

export const SkillsBadgeSection: React.FC<SkillsBadgeSectionProps> = ({
  activeSkills,
  skillsMeta,
  skillsService,
  onAddSkill,
  onRemoveSkill,
}) => {
  const { t } = useLanguage();

  return (
    <>
      <div className="mb-3">
        <Form.Label className="small fw-bold">{t(VocabSkills, "search_skill")}</Form.Label>
        <GenericSearchBar<SkillRead>
          searchFunction={(query: string, signal?: AbortSignal) => skillsService.searchSkills(query, signal)}
          renderLabel={(skill) => skill.name}
          placeholder={t(VocabSkills, "search_skill_placeholder")}
          onSelect={onAddSkill}
        />
      </div>

      <Card className="bg-light border-0 rounded-3 mt-4">
        <Card.Body>
          <Form.Label className="small text-muted mb-2">{t(VocabSkills, "current_skills")}</Form.Label>
          <div className="d-flex flex-wrap gap-2">
            {activeSkills.map((id) => {
              const info = skillsMeta?.[id];
              const catKey = info ? Number(info.category) : 0;
              const badgeColor = ColorSpecialties[catKey] || ColorSpecialties[0];

              return (
                <Badge
                  key={id}
                  pill
                  className="p-2 d-flex align-items-center fw-normal"
                  style={{
                    backgroundColor: info ? badgeColor : "#dee2e6",
                    color: info ? "#ffffff" : "#6c757d",
                    fontSize: "0.875rem",
                  }}
                >
                  {info ? info.name : "..."}
                  <XCircleFill
                    className="ms-2 cursor-pointer"
                    style={{ cursor: "pointer" }}
                    onClick={() => onRemoveSkill(id)}
                  />
                </Badge>
              );
            })}

            {activeSkills.length === 0 && (
              <small className="text-muted fst-italic">{t(VocabSkills, "no_selection")}</small>
            )}
          </div>
        </Card.Body>
      </Card>
    </>
  );
};